/**
 * C10 Generation API Service
 *
 * Handles C10 file generation, download, and filing status updates.
 *
 * Session 4.3: Step 4 - Generate & Download
 */

import { apiClient } from './client';

// =============================================================================
// REQUEST/RESPONSE TYPES
// =============================================================================

/**
 * Employee data for C10 generation.
 */
export interface GenerateEmployeeData {
  employee_ni: string;
  last_name: string;
  first_name: string;
  middle_initial?: string;
  basic_wages: number;
  gratuity_wages?: number;
  weeks_worked: number;
  pay_indicator: string;
  category: number;
}

/**
 * Request to generate a C10 file.
 */
export interface GenerateC10Request {
  employer_ni: string;
  contribution_month: number;
  contribution_year: number;
  format_type: 'HOSPITALITY' | 'NON_HOSPITALITY';
  employees: GenerateEmployeeData[];
}

/**
 * Summary of generated C10 contributions.
 */
export interface GenerationSummary {
  employee_count: number;
  total_wages: number;
  total_contributions: number;
  employee_portion: number;
  employer_portion: number;
  gratuity_portion: number;
}

/**
 * Response from C10 generation.
 */
export interface GenerateC10Response {
  success: boolean;
  data: {
    file_url: string;
    filename: string;
    filing_id: string;
    summary: GenerationSummary;
  };
}

// =============================================================================
// API SERVICE
// =============================================================================

export const generationApi = {
  /**
   * Generate C10 file from employee data.
   */
  async generateC10(request: GenerateC10Request): Promise<GenerateC10Response> {
    return apiClient.post<GenerateC10Response>('/v1/nib/generate', request);
  },

  /**
   * Download generated C10 file as blob.
   */
  async downloadC10(fileUrl: string): Promise<Blob> {
    return apiClient.get<Blob>(fileUrl, {
      responseType: 'blob',
    });
  },

  /**
   * Mark filing as submitted to NIB.
   */
  async markAsFiled(filingId: string): Promise<void> {
    await apiClient.post(`/v1/filings/${filingId}/mark-filed`);
  },
};
